import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { RotateCcwIcon, SearchIcon } from 'lucide-react';
import { movementLabel } from './pharmacyUtils';
export function StockMovementHistory() {
  const {
    currentUser,
    stockMovements,
    patients,
    reverseDispense
  } = useData();
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState('');
  const [reversing, setReversing] = useState<string | null>(null);
  const manager = currentUser?.role === 'Pharmacy Manager';
  const movements = useMemo(() => stockMovements.filter(movement => {
    const patient = movement.patientId ? patients.find(item => item.id === movement.patientId) : undefined;
    return `${movement.medicineName} ${movement.type} ${movement.actionPerformed || ''} ${movement.actorName} ${movement.authorisedUserName || ''} ${movement.batchNumber || ''} ${movement.notes || ''} ${movement.reason || ''} ${movement.prescriptionId || ''} ${patient?.firstName || ''} ${patient?.lastName || ''}`.toLowerCase().includes(query.trim().toLowerCase());
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()), [patients, query, stockMovements]);
  const reverse = async (movementId: string) => {
    const comment = window.prompt('Enter the mandatory manager comment for this dispensing reversal:');
    if (!comment) return;
    setReversing(movementId);
    const result = await reverseDispense(movementId, comment);
    setReversing(null);
    setMessage(result.ok ? 'Dispensing was reversed with a compensating inventory movement. The prescription is pending again.' : result.error || 'Unable to reverse dispensing.');
  };
  return <div className="space-y-5" data-id="element-5116">
      <div data-id="element-5117">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-5118">
          Stock Movement History
        </h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-5119">
          Permanent, live audit trail for every inventory transaction. Ledger
          records cannot be deleted from this workspace.
        </p>
      </div>
      {message && <p role="status" className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700" data-id="element-5120">
          {message}
        </p>}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-5121">
        <div className="border-b border-slate-100 p-4" data-id="element-5122">
          <div className="relative max-w-xl" data-id="element-5123">
            <SearchIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-5124" />
            <input aria-label="Search stock movements" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search medicine, batch, action, user, authoriser, patient or reference" className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600" data-id="element-5125" />
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-5126">
          <table className="min-w-[1550px] w-full divide-y divide-slate-100" data-id="element-5127">
            <thead className="bg-slate-50" data-id="element-5128">
              <tr data-id="element-5129">
                {['Date & time', 'Medicine / batch allocations', 'Action / reason', 'Previous → new', 'Changed', 'Responsible / authorised', 'Patient / reference', 'Comment', ''].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-5130">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-5131">
              {movements.map(movement => {
              const patient = movement.patientId ? patients.find(item => item.id === movement.patientId) : undefined;
              const canReverse = manager && movement.type === 'Dispense' && !stockMovements.some(item => item.reversalOfMovementId === movement.id);
              return <tr key={movement.id} className="align-top" data-id="element-5132">
                    <td className="whitespace-nowrap px-5 py-3.5 text-xs text-slate-600" data-id="element-5133">
                      {new Date(movement.createdAt).toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5" data-id="element-5134">
                      <p className="text-sm font-semibold text-slate-900" data-id="element-5135">
                        {movement.medicineName}
                      </p>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5136">
                        {movement.allocations?.length ? movement.allocations.map(allocation => `${allocation.batchNumber} (${allocation.quantityBefore}→${allocation.quantityAfter})`).join(', ') : movement.batchNumber || 'No batch'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5" data-id="element-5137">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700" data-id="element-5138">
                        {movement.actionPerformed || movementLabel(movement)}
                      </span>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5139">
                        Reason: {movement.reason || '—'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm font-medium text-slate-700" data-id="element-5140">
                      {movement.quantityBefore} → {movement.quantityAfter}
                    </td>
                    <td className={`px-5 py-3.5 text-sm font-bold ${movement.quantityDelta > 0 ? 'text-emerald-700' : 'text-red-700'}`} data-id="element-5141">
                      {movement.quantityDelta > 0 ? '+' : ''}
                      {movement.quantityDelta}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700" data-id="element-5142">
                      <p data-id="element-5143">{movement.actorName}</p>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5144">
                        Authorised: {movement.authorisedUserName || '—'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700" data-id="element-5145">
                      {patient ? <p data-id="element-5146">
                          {patient.firstName} {patient.lastName}
                        </p> : <p data-id="element-5147">—</p>}
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5148">
                        {movement.prescriptionId ? `Rx: ${movement.prescriptionId}` : movement.referenceId ? `Ref: ${movement.referenceId}` : '—'}
                      </p>
                    </td>
                    <td className="max-w-xs px-5 py-3.5 text-sm text-slate-600" data-id="element-5149">
                      {movement.notes || '—'}
                    </td>
                    <td className="px-5 py-3.5 text-right" data-id="element-5150">
                      {canReverse && <button type="button" disabled={reversing === movement.id} onClick={() => reverse(movement.id)} className="inline-flex items-center whitespace-nowrap rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50" data-id="element-5151">
                          <RotateCcwIcon className="mr-1.5 h-3.5 w-3.5" data-id="element-5152" />
                          {reversing === movement.id ? 'Reversing…' : 'Reverse dispense'}
                        </button>}
                      {movement.reversalOfMovementId && <span className="text-xs font-semibold text-emerald-700" data-id="element-5153">
                          Reversal
                        </span>}
                    </td>
                  </tr>;
            })}
              {!movements.length && <tr data-id="element-5154">
                  <td colSpan={9} className="px-5 py-14 text-center text-sm text-slate-500" data-id="element-5155">
                    No stock movements match your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}